import { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { BarChart3, Users, ShoppingBag, FileText, Bell, Mail, Settings, DollarSign, TrendingUp, Package, Tag, Shield as ShieldIcon } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { toast } from 'sonner';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function StatCard({ label, value, icon: Icon, color }) {
  return (
    <div className="bg-white border border-gray-200 rounded-lg p-5">
      <div className="flex items-center justify-between mb-2">
        <span className="text-xs text-gray-500 font-body uppercase tracking-wide">{label}</span>
        <Icon className={`w-5 h-5 ${color}`} />
      </div>
      <p className="text-2xl font-heading font-bold text-navy">{value}</p>
    </div>
  );
}

export default function AdminPanel() {
  const { user } = useAuth();
  const [analytics, setAnalytics] = useState(null);
  const [members, setMembers] = useState([]);
  const [orders, setOrders] = useState([]);
  const [contacts, setContacts] = useState([]);
  const [products, setProducts] = useState([]);
  const [newAnn, setNewAnn] = useState({ title: '', message: '' });
  const [newNews, setNewNews] = useState({ title: '', content: '', summary: '' });
  const [newEvent, setNewEvent] = useState({ title: '', description: '', date: '', location: '' });
  const [newProduct, setNewProduct] = useState({ name: '', description: '', price: '', category: 'Apparel', sizes: '', colors: '', inventory: 100 });
  const [editMember, setEditMember] = useState(null);
  const [memberSearch, setMemberSearch] = useState('');

  const loadData = useCallback(async () => {
    const cfg = { withCredentials: true };
    try {
      const [a, m, o, c, p] = await Promise.all([
        axios.get(`${API}/admin/analytics`, cfg), axios.get(`${API}/admin/members`, cfg),
        axios.get(`${API}/admin/orders`, cfg), axios.get(`${API}/admin/contacts`, cfg),
        axios.get(`${API}/products`),
      ]);
      setAnalytics(a.data); setMembers(m.data); setOrders(o.data); setContacts(c.data); setProducts(p.data);
    } catch { toast.error('Failed to load data'); }
  }, []);

  useEffect(() => { loadData(); }, [loadData]);

  const handleAnnouncement = async () => {
    if (!newAnn.title || !newAnn.message) return;
    try {
      await axios.post(`${API}/admin/announcements`, newAnn, { withCredentials: true });
      toast.success('Announcement sent'); setNewAnn({ title: '', message: '' });
    } catch { toast.error('Failed'); }
  };

  const handleNews = async () => {
    if (!newNews.title || !newNews.content) return;
    try {
      await axios.post(`${API}/admin/news`, newNews, { withCredentials: true });
      toast.success('Article published'); setNewNews({ title: '', content: '', summary: '' }); loadData();
    } catch { toast.error('Failed'); }
  };

  const handleEvent = async () => {
    if (!newEvent.title || !newEvent.date) return;
    try {
      await axios.post(`${API}/admin/events`, newEvent, { withCredentials: true });
      toast.success('Event created'); setNewEvent({ title: '', description: '', date: '', location: '' }); loadData();
    } catch { toast.error('Failed'); }
  };

  const handleProduct = async () => {
    if (!newProduct.name || !newProduct.price) return;
    try {
      const payload = { ...newProduct, price: parseFloat(newProduct.price), inventory: parseInt(newProduct.inventory) || 100, sizes: newProduct.sizes.split(',').map(s => s.trim()).filter(Boolean), colors: newProduct.colors.split(',').map(s => s.trim()).filter(Boolean) };
      await axios.post(`${API}/admin/products`, payload, { withCredentials: true });
      toast.success('Product added'); setNewProduct({ name: '', description: '', price: '', category: 'Apparel', sizes: '', colors: '', inventory: 100 }); loadData();
    } catch { toast.error('Failed'); }
  };

  const handleOrderUpdate = async (orderId, status, tracking = '') => {
    try {
      await axios.put(`${API}/admin/orders/${orderId}`, { status, tracking_number: tracking }, { withCredentials: true });
      toast.success('Order updated'); loadData();
    } catch { toast.error('Failed'); }
  };

  const handleMemberUpdate = async () => {
    if (!editMember) return;
    try {
      await axios.put(`${API}/admin/members/${editMember._id}`, {
        membership_status: editMember.membership_status, notes: editMember.notes, tags: editMember.tags
      }, { withCredentials: true });
      toast.success('Member updated'); setEditMember(null); loadData();
    } catch { toast.error('Failed'); }
  };

  const filteredMembers = members.filter(m => !memberSearch || m.name?.toLowerCase().includes(memberSearch.toLowerCase()) || m.email?.toLowerCase().includes(memberSearch.toLowerCase()));

  return (
    <div className="pt-16 min-h-screen bg-[#FAFAFA]" data-testid="admin-panel">
      <div className="max-w-7xl mx-auto px-6 lg:px-8 py-10">
        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="text-2xl font-heading font-bold text-navy">Admin Panel</h1>
            <p className="text-sm text-gray-600 font-body">Welcome, {user?.name}</p>
          </div>
          <div className="flex items-center gap-3">
            <Link to="/admin/compliance" data-testid="admin-compliance-link">
              <Button variant="outline" className="font-body text-xs border-navy text-navy hover:bg-navy/5">
                <ShieldIcon className="w-3 h-3 mr-1" /> Compliance & Audit
              </Button>
            </Link>
            <Badge className="bg-navy text-white font-body">Admin</Badge>
          </div>
        </div>

        {/* Analytics */}
        {analytics && (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6" data-testid="admin-analytics">
            <StatCard label="Total Members" value={analytics.total_members} icon={Users} color="text-navy" />
            <StatCard label="Active Members" value={analytics.active_members} icon={TrendingUp} color="text-forest" />
            <StatCard label="Total Revenue" value={`$${analytics.total_revenue?.toLocaleString()}`} icon={DollarSign} color="text-gold" />
            <StatCard label="Pending Orders" value={analytics.pending_orders} icon={Package} color="text-red-500" />
          </div>
        )}

        <Tabs defaultValue="members" className="space-y-4">
          <TabsList className="bg-white border border-gray-200 rounded-lg p-1 flex-wrap" data-testid="admin-tabs">
            <TabsTrigger value="members" className="font-body text-xs" data-testid="admin-tab-members"><Users className="w-3 h-3 mr-1" />Members</TabsTrigger>
            <TabsTrigger value="orders" className="font-body text-xs" data-testid="admin-tab-orders"><ShoppingBag className="w-3 h-3 mr-1" />Orders</TabsTrigger>
            <TabsTrigger value="products" className="font-body text-xs" data-testid="admin-tab-products"><Tag className="w-3 h-3 mr-1" />Products</TabsTrigger>
            <TabsTrigger value="content" className="font-body text-xs" data-testid="admin-tab-content"><FileText className="w-3 h-3 mr-1" />Content</TabsTrigger>
            <TabsTrigger value="messages" className="font-body text-xs" data-testid="admin-tab-messages"><Mail className="w-3 h-3 mr-1" />Messages</TabsTrigger>
          </TabsList>

          {/* Members Tab */}
          <TabsContent value="members" data-testid="admin-members-tab">
            <div className="bg-white border border-gray-200 rounded-lg p-5">
              <div className="flex items-center justify-between mb-4">
                <h3 className="font-heading font-bold text-navy">Member CRM</h3>
                <Input placeholder="Search members..." value={memberSearch} onChange={e => setMemberSearch(e.target.value)} className="w-60 font-body text-sm" data-testid="member-search-input" />
              </div>

              {editMember ? (
                <div className="border border-gold/30 bg-gold/5 rounded-lg p-5 mb-4" data-testid="edit-member-form">
                  <h4 className="font-heading font-bold text-navy mb-3">Edit: {editMember.name}</h4>
                  <div className="grid sm:grid-cols-2 gap-3">
                    <div>
                      <Label className="font-body text-xs">Status</Label>
                      <select value={editMember.membership_status || 'pending'} onChange={e => setEditMember(p => ({ ...p, membership_status: e.target.value }))} className="w-full border rounded-md p-2 text-sm font-body mt-1">
                        <option value="pending">Pending</option><option value="active">Active</option><option value="expired">Expired</option>
                      </select>
                    </div>
                    <div>
                      <Label className="font-body text-xs">Tags (comma separated)</Label>
                      <Input value={(editMember.tags || []).join(', ')} onChange={e => setEditMember(p => ({ ...p, tags: e.target.value.split(',').map(s => s.trim()).filter(Boolean) }))} className="mt-1 font-body text-sm" />
                    </div>
                    <div className="sm:col-span-2">
                      <Label className="font-body text-xs">Notes</Label>
                      <Textarea value={editMember.notes || ''} onChange={e => setEditMember(p => ({ ...p, notes: e.target.value }))} rows={2} className="mt-1 font-body text-sm" />
                    </div>
                  </div>
                  <div className="flex gap-2 mt-3">
                    <Button onClick={handleMemberUpdate} className="btn-primary-safe font-body text-xs" data-testid="save-member-btn">Save</Button>
                    <Button onClick={() => setEditMember(null)} variant="outline" className="font-body text-xs">Cancel</Button>
                  </div>
                </div>
              ) : null}

              <div className="overflow-x-auto">
                <table className="w-full text-sm font-body">
                  <thead><tr className="border-b border-gray-200">
                    <th className="text-left py-2 text-gray-500 font-medium">Name</th>
                    <th className="text-left py-2 text-gray-500 font-medium">Email</th>
                    <th className="text-left py-2 text-gray-500 font-medium">Status</th>
                    <th className="text-left py-2 text-gray-500 font-medium">Joined</th>
                    <th className="text-left py-2 text-gray-500 font-medium">Actions</th>
                  </tr></thead>
                  <tbody>
                    {filteredMembers.map(m => (
                      <tr key={m._id} className="border-b border-gray-100">
                        <td className="py-2 font-medium text-navy">{m.name}</td>
                        <td className="py-2 text-gray-600">{m.email}</td>
                        <td className="py-2"><Badge className={m.membership_status === 'active' ? 'bg-forest/20 text-forest' : 'bg-gold/20 text-gold'}>{m.membership_status || 'pending'}</Badge></td>
                        <td className="py-2 text-gray-500">{m.created_at ? new Date(m.created_at).toLocaleDateString() : '-'}</td>
                        <td className="py-2"><Button onClick={() => setEditMember(m)} variant="outline" size="sm" className="font-body text-xs" data-testid={`edit-member-${m._id}`}>Edit</Button></td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </TabsContent>

          {/* Orders Tab */}
          <TabsContent value="orders" data-testid="admin-orders-tab">
            <div className="bg-white border border-gray-200 rounded-lg p-5">
              <h3 className="font-heading font-bold text-navy mb-4">Orders ({orders.length})</h3>
              <div className="overflow-x-auto">
                <table className="w-full text-sm font-body">
                  <thead><tr className="border-b border-gray-200">
                    <th className="text-left py-2 text-gray-500 font-medium">Order #</th>
                    <th className="text-left py-2 text-gray-500 font-medium">Customer</th>
                    <th className="text-left py-2 text-gray-500 font-medium">Total</th>
                    <th className="text-left py-2 text-gray-500 font-medium">Status</th>
                    <th className="text-left py-2 text-gray-500 font-medium">Date</th>
                    <th className="text-left py-2 text-gray-500 font-medium">Actions</th>
                  </tr></thead>
                  <tbody>
                    {orders.map(o => (
                      <tr key={o.id} className="border-b border-gray-100">
                        <td className="py-2 font-medium text-navy">{o.order_number}</td>
                        <td className="py-2">{o.user_name || o.user_email}</td>
                        <td className="py-2 font-bold">${o.total?.toFixed(2)}</td>
                        <td className="py-2"><Badge className={o.status === 'shipped' ? 'bg-forest/20 text-forest' : o.status === 'fulfilled' ? 'bg-navy/20 text-navy' : 'bg-gold/20 text-gold'}>{o.status}</Badge></td>
                        <td className="py-2 text-gray-500">{new Date(o.created_at).toLocaleDateString()}</td>
                        <td className="py-2">
                          <select value={o.status} onChange={e => handleOrderUpdate(o.id, e.target.value)} className="border rounded text-xs p-1 font-body" data-testid={`order-status-${o.id}`}>
                            <option value="pending">Pending</option><option value="fulfilled">Fulfilled</option><option value="shipped">Shipped</option>
                          </select>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </TabsContent>

          {/* Products Tab */}
          <TabsContent value="products" data-testid="admin-products-tab">
            <div className="bg-white border border-gray-200 rounded-lg p-5">
              <h3 className="font-heading font-bold text-navy mb-4">Add Product</h3>
              <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3 mb-6">
                <div><Label className="font-body text-xs">Name</Label><Input value={newProduct.name} onChange={e => setNewProduct(p => ({ ...p, name: e.target.value }))} className="mt-1 font-body text-sm" data-testid="new-product-name" /></div>
                <div><Label className="font-body text-xs">Price</Label><Input type="number" value={newProduct.price} onChange={e => setNewProduct(p => ({ ...p, price: e.target.value }))} className="mt-1 font-body text-sm" data-testid="new-product-price" /></div>
                <div><Label className="font-body text-xs">Category</Label>
                  <select value={newProduct.category} onChange={e => setNewProduct(p => ({ ...p, category: e.target.value }))} className="w-full border rounded-md p-2 text-sm font-body mt-1"><option>Apparel</option><option>Yard Signs & Stickers</option><option>Accessories</option><option>Branded Items</option></select>
                </div>
                <div><Label className="font-body text-xs">Sizes (comma sep)</Label><Input value={newProduct.sizes} onChange={e => setNewProduct(p => ({ ...p, sizes: e.target.value }))} className="mt-1 font-body text-sm" placeholder="S,M,L,XL" /></div>
                <div><Label className="font-body text-xs">Colors (comma sep)</Label><Input value={newProduct.colors} onChange={e => setNewProduct(p => ({ ...p, colors: e.target.value }))} className="mt-1 font-body text-sm" placeholder="Navy,White" /></div>
                <div><Label className="font-body text-xs">Inventory</Label><Input type="number" value={newProduct.inventory} onChange={e => setNewProduct(p => ({ ...p, inventory: e.target.value }))} className="mt-1 font-body text-sm" /></div>
                <div className="sm:col-span-2"><Label className="font-body text-xs">Description</Label><Input value={newProduct.description} onChange={e => setNewProduct(p => ({ ...p, description: e.target.value }))} className="mt-1 font-body text-sm" /></div>
              </div>
              <Button onClick={handleProduct} className="btn-primary-safe font-body text-sm" data-testid="add-product-btn">Add Product</Button>

              <h3 className="font-heading font-bold text-navy mt-8 mb-4">Current Products ({products.length})</h3>
              <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
                {products.map(p => (
                  <div key={p.id} className="border border-gray-200 rounded-lg p-3">
                    <p className="font-body font-bold text-navy text-sm">{p.name}</p>
                    <p className="text-xs text-gray-500 font-body">{p.category} | ${p.price}</p>
                    <p className="text-xs text-gray-400 font-body">Inv: {p.inventory}</p>
                  </div>
                ))}
              </div>
            </div>
          </TabsContent>

          {/* Content Tab */}
          <TabsContent value="content" data-testid="admin-content-tab">
            <div className="space-y-6">
              {/* Announcements */}
              <div className="bg-white border border-gray-200 rounded-lg p-5">
                <h3 className="font-heading font-bold text-navy mb-3 flex items-center gap-2"><Bell className="w-4 h-4 text-gold" /> Broadcast Announcement</h3>
                <div className="space-y-3">
                  <Input placeholder="Title" value={newAnn.title} onChange={e => setNewAnn(p => ({ ...p, title: e.target.value }))} className="font-body text-sm" data-testid="announcement-title-input" />
                  <Textarea placeholder="Message" value={newAnn.message} onChange={e => setNewAnn(p => ({ ...p, message: e.target.value }))} rows={3} className="font-body text-sm" data-testid="announcement-message-input" />
                  <Button onClick={handleAnnouncement} className="btn-primary-safe font-body text-sm" data-testid="send-announcement-btn">Send Announcement</Button>
                </div>
              </div>

              {/* News */}
              <div className="bg-white border border-gray-200 rounded-lg p-5">
                <h3 className="font-heading font-bold text-navy mb-3 flex items-center gap-2"><FileText className="w-4 h-4 text-forest" /> Publish News Article</h3>
                <div className="space-y-3">
                  <Input placeholder="Title" value={newNews.title} onChange={e => setNewNews(p => ({ ...p, title: e.target.value }))} className="font-body text-sm" data-testid="news-title-input" />
                  <Input placeholder="Summary" value={newNews.summary} onChange={e => setNewNews(p => ({ ...p, summary: e.target.value }))} className="font-body text-sm" />
                  <Textarea placeholder="Full content" value={newNews.content} onChange={e => setNewNews(p => ({ ...p, content: e.target.value }))} rows={4} className="font-body text-sm" data-testid="news-content-input" />
                  <Button onClick={handleNews} className="btn-secondary-safe font-body text-sm" data-testid="publish-news-btn">Publish Article</Button>
                </div>
              </div>

              {/* Events */}
              <div className="bg-white border border-gray-200 rounded-lg p-5">
                <h3 className="font-heading font-bold text-navy mb-3 flex items-center gap-2"><Settings className="w-4 h-4 text-navy" /> Create Event</h3>
                <div className="grid sm:grid-cols-2 gap-3">
                  <div><Label className="font-body text-xs">Title</Label><Input value={newEvent.title} onChange={e => setNewEvent(p => ({ ...p, title: e.target.value }))} className="mt-1 font-body text-sm" data-testid="event-title-input" /></div>
                  <div><Label className="font-body text-xs">Date</Label><Input type="date" value={newEvent.date} onChange={e => setNewEvent(p => ({ ...p, date: e.target.value }))} className="mt-1 font-body text-sm" data-testid="event-date-input" /></div>
                  <div><Label className="font-body text-xs">Location</Label><Input value={newEvent.location} onChange={e => setNewEvent(p => ({ ...p, location: e.target.value }))} className="mt-1 font-body text-sm" /></div>
                  <div><Label className="font-body text-xs">Description</Label><Input value={newEvent.description} onChange={e => setNewEvent(p => ({ ...p, description: e.target.value }))} className="mt-1 font-body text-sm" /></div>
                </div>
                <Button onClick={handleEvent} className="btn-primary-safe font-body text-sm mt-3" data-testid="create-event-btn">Create Event</Button>
              </div>
            </div>
          </TabsContent>

          {/* Messages Tab */}
          <TabsContent value="messages" data-testid="admin-messages-tab">
            <div className="bg-white border border-gray-200 rounded-lg p-5">
              <h3 className="font-heading font-bold text-navy mb-4">Contact Messages ({contacts.length})</h3>
              {contacts.length === 0 ? <p className="text-sm text-gray-500 font-body">No messages</p> : (
                <div className="space-y-3">
                  {contacts.map(c => (
                    <div key={c.id} className="border border-gray-200 rounded-lg p-4">
                      <div className="flex items-start justify-between">
                        <div>
                          <p className="font-body font-bold text-navy text-sm">{c.name} &lt;{c.email}&gt;</p>
                          {c.subject && <p className="text-xs text-gold font-body">{c.subject}</p>}
                        </div>
                        <span className="text-xs text-gray-400 font-body">{new Date(c.created_at).toLocaleDateString()}</span>
                      </div>
                      <p className="text-sm text-gray-700 font-body mt-2">{c.message}</p>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
}
